import './styles.css';

import { initialFacts, type Answer } from './domain/facts';
import {
  answer,
  back,
  canGoBack,
  nextQuestion,
  progress,
  type RunState,
} from './domain/engine';
import { toSearchRequest, typeLabel, widenFacts } from './domain/mapping';
import { mealAt } from './domain/meal';
import { buildQuestions, type Mode, type QuestionSet } from './domain/questions';
import { rankPlaces, type ScoredPlace } from './domain/rank';
import {
  DEFAULT_MAX_RESULTS,
  type BudgetStatus,
  type GeocodeResult,
  type LatLng,
  type SearchRequest,
  type UserUsage,
} from './domain/types';
import { DEFAULT_UNITS, type Units } from './domain/units';
import {
  ApiError,
  changePassword,
  geocode,
  getSession,
  login,
  logout,
  search,
} from './services/api';
import { readCached, writeCached } from './services/cache';
import {
  LocationError,
  isStale,
  readStoredLocation,
  requestGeolocation,
  storeLocation,
  type LocationSource,
  type StoredLocation,
} from './services/location';
import {
  readStoredMode,
  readStoredUnits,
  storeMode,
  storeUnits,
} from './services/preferences';
import { budgetBanner, sharedAccountBanner } from './ui/banner';
import { clear, el, spinner } from './ui/dom';
import { locationView } from './ui/location';
import { loginView, type LoginView } from './ui/login';
import { passwordView, type PasswordView } from './ui/password';
import { questionView } from './ui/question';
import { TOP_N, resultsView } from './ui/results';
import { unitsToggle } from './ui/units';
import { usageSummary } from './ui/usage';

type Screen = 'boot' | 'login' | 'password' | 'location' | 'question' | 'results';

/** Where the search runs from, plus what it took to know that - see `refreshGps`. */
interface AppLocation {
  coords: LatLng;
  label: string;
  source: LocationSource;
  /** When the fix was taken, in epoch ms. */
  at: number;
}

function toAppLocation(stored: StoredLocation): AppLocation {
  return {
    coords: stored.location,
    label: stored.label,
    source: stored.source,
    at: stored.at,
  };
}

interface AppState {
  screen: Screen;
  user: string | null;
  /** Whether to offer the dashboard link. `/api/admin/*` is what actually gates it. */
  admin: boolean;
  /** Signed in, but every route bar the change form is refused until this clears. */
  mustChangePassword: boolean;
  budget: BudgetStatus | null;
  /** This account's own allowances, as opposed to whichever limit `budget` names. */
  usage: UserUsage | null;
  location: AppLocation | null;
  run: RunState;
  mode: Mode;
  units: Units;
  questions: QuestionSet;
  places: ScoredPlace[];
  shown: number;
  /**
   * Deployment-wide ceiling on the results list, from `/api/session`. Held in state
   * rather than as a constant because an admin can change it while the app is open.
   */
  maxResults: number;
  cached: boolean;
  resultStatus: 'loading' | 'ready' | 'error';
  error: string | null;
  pending: boolean;
  locationStatus: 'idle' | 'locating' | 'searching';
  /** Set when an empty result was retried one step broader - see runSearch. */
  widened: { from: string; to: string } | null;
  geocodeResults: GeocodeResult[];
  lastRequest: SearchRequest | null;
}

const root = document.getElementById('app');
if (!root) throw new Error('#app is missing');

/** Simple until the user says otherwise: advanced costs more billed calls per search. */
const DEFAULT_MODE: Mode = readStoredMode() ?? 'simple';

const STARTING_UNITS: Units = readStoredUnits() ?? DEFAULT_UNITS;

const state: AppState = {
  screen: 'boot',
  user: null,
  admin: false,
  mustChangePassword: false,
  budget: null,
  usage: null,
  location: null,
  run: { facts: initialFacts(), asked: [] },
  mode: DEFAULT_MODE,
  units: STARTING_UNITS,
  questions: buildQuestions(DEFAULT_MODE, STARTING_UNITS),
  places: [],
  shown: TOP_N,
  maxResults: DEFAULT_MAX_RESULTS,
  cached: false,
  resultStatus: 'loading',
  error: null,
  pending: false,
  locationStatus: 'idle',
  geocodeResults: [],
  widened: null,
  lastRequest: null,
};

function set(patch: Partial<AppState>): void {
  Object.assign(state, patch);
  render();
}

function resetRun(): void {
  state.run = { facts: initialFacts(), asked: [] };
  state.questions = buildQuestions(state.mode, state.units);
  state.places = [];
  state.shown = TOP_N;
  state.error = null;
  state.cached = false;
  state.widened = null;
  state.lastRequest = null;
}

/** A 401 anywhere means the session lapsed; drop back to the login screen cleanly. */
function handleApiError(error: unknown): void {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      resetRun();
      set({
        screen: 'login',
        user: null,
        admin: false,
        mustChangePassword: false,
        error: null,
        budget: null,
        usage: null,
        // Cleared because a lookup in flight when this lands never gets to finish -
        // otherwise the location screen comes back still saying it is searching.
        locationStatus: 'idle',
      });
      return;
    }
    // Every route answers this while a change is outstanding, so any of them can be
    // what discovers it - a session that was fine a moment ago can be reset by an
    // admin mid-run.
    if (error.passwordChangeRequired) {
      resetRun();
      set({
        screen: 'password',
        mustChangePassword: true,
        error: null,
        locationStatus: 'idle',
      });
      return;
    }
    set({
      resultStatus: 'error',
      error: error.message,
      budget: error.budget ?? state.budget,
      usage: error.usage ?? state.usage,
    });
    return;
  }
  set({
    resultStatus: 'error',
    error: 'Could not reach the server. Check your connection.',
  });
}

// ---------------------------------------------------------------- actions

async function boot(): Promise<void> {
  try {
    const session = await getSession();
    if (!session) {
      set({ screen: 'login' });
      return;
    }
    const stored = readStoredLocation();
    set({
      user: session.user,
      admin: session.admin,
      mustChangePassword: session.mustChangePassword,
      budget: session.budget,
      usage: session.usage,
      maxResults: session.maxResults ?? DEFAULT_MAX_RESULTS,
      location: stored ? toAppLocation(stored) : null,
      screen: session.mustChangePassword ? 'password' : stored ? 'question' : 'location',
    });
    if (!session.mustChangePassword) void refreshGps();
  } catch {
    // Offline on a cold start: the shell is cached, so let them sign in and retry.
    set({ screen: 'login', error: 'Could not reach the server.' });
  }
}

async function doLogin(username: string, password: string): Promise<void> {
  set({ pending: true, error: null });
  try {
    const signedIn = await login(username, password);

    // Awaited rather than left to `refreshBudget`, because the answer decides which
    // screen comes next. Letting it arrive late would paint the question screen and
    // then yank it away the moment the first search 403'd.
    //
    // Retried once when it comes back signed-out: the reply above is what sets the
    // cookie, and a browser that has not committed it yet answers this call 401.
    // Nothing here depends on that answer any more, but the masthead's dashboard link
    // and the budget figures would otherwise be missing for the rest of the session.
    const session = (await getSession()) ?? (await getSession());
    const stored = readStoredLocation();
    // The login reply is the authority. An absent field means a deployment that
    // predates it, and only then does the session call get to answer - a session that
    // came back signed-out says nothing either way, and must not read as "no change
    // due", which is what used to let a new account walk straight into the app.
    const mustChangePassword =
      signedIn.mustChangePassword ?? session?.mustChangePassword === true;

    set({
      pending: false,
      user: signedIn.user,
      admin: session?.admin === true,
      mustChangePassword,
      budget: session?.budget ?? null,
      usage: session?.usage ?? null,
      maxResults: session?.maxResults ?? DEFAULT_MAX_RESULTS,
      error: null,
      location: stored ? toAppLocation(stored) : null,
      screen: mustChangePassword ? 'password' : stored ? 'question' : 'location',
    });
    if (!mustChangePassword) void refreshGps();
  } catch (error) {
    set({
      pending: false,
      error: error instanceof ApiError ? error.message : 'Could not sign in.',
    });
  }
}

/**
 * Replace the password, then carry on into the app.
 *
 * The reply clears `mustChangePassword` server-side, so the next call to anything
 * succeeds; there is nothing to re-fetch beyond the budget the masthead shows.
 */
async function doChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  set({ pending: true, error: null });
  try {
    await changePassword(currentPassword, newPassword);
    const stored = readStoredLocation();
    set({
      pending: false,
      mustChangePassword: false,
      error: null,
      location: stored ? toAppLocation(stored) : null,
      screen: stored ? 'question' : 'location',
    });
    void refreshBudget();
    void refreshGps();
  } catch (error) {
    set({
      pending: false,
      error:
        error instanceof ApiError ? error.message : 'Could not change your password.',
    });
  }
}

/**
 * Also where the admin flag arrives after a sign-in: `/api/login` answers with the
 * username alone, and this call is already made on the way in, so the masthead learns
 * whether to offer the dashboard without a round trip of its own.
 */
async function refreshBudget(): Promise<void> {
  try {
    const session = await getSession();
    if (session) {
      set({
        budget: session.budget,
        usage: session.usage,
        admin: session.admin,
        mustChangePassword: session.mustChangePassword,
        // Picked up here too: an admin can change the results ceiling mid-session, and
        // this call already runs after every search.
        maxResults: session.maxResults ?? DEFAULT_MAX_RESULTS,
      });
    }
  } catch {
    // Budget display is advisory; a failure here shouldn't interrupt anything.
  }
}

async function useGps(): Promise<void> {
  set({ locationStatus: 'locating', error: null });
  try {
    const coords = await requestGeolocation();
    const entry = storeLocation(coords, 'you', 'gps');
    set({
      location: toAppLocation(entry),
      locationStatus: 'idle',
      screen: 'question',
      error: null,
    });
  } catch (error) {
    set({
      locationStatus: 'idle',
      error:
        error instanceof LocationError
          ? `${error.message}. Type where you are instead.`
          : 'Could not get your location.',
    });
  }
}

/** Non-null exactly while a GPS refresh is in flight, so callers can wait on it. */
let gpsRefresh: Promise<void> | null = null;

/**
 * Take the GPS fix again when the one on file has gone stale.
 *
 * The stored position is whatever the phone read the first time it was asked, and on an
 * installed PWA that can be days and a hundred miles ago - nothing here reloads on its
 * own. So the fix is refreshed on the way in, on the way back from the background, and
 * at the top of every new run, quietly: the answer is only needed when the search goes
 * out, and by then several taps have usually passed.
 *
 * A typed-in place is left alone - it was chosen, not measured - and a failed read keeps
 * the old position rather than dropping the user back on the location screen. It is the
 * same permission that was already granted, so this asks nothing of them.
 */
function refreshGps(): Promise<void> {
  const current = state.location;
  if (!current || !isStale(current)) return Promise.resolve();
  if (gpsRefresh) return gpsRefresh;

  gpsRefresh = requestGeolocation()
    .then((coords) => {
      const entry = storeLocation(coords, current.label, 'gps');
      set({ location: toAppLocation(entry) });
    })
    .catch(() => {
      // Moved indoors, or permission since revoked. The last known position still
      // beats interrupting a run that is already under way.
    })
    .finally(() => {
      gpsRefresh = null;
    });

  return gpsRefresh;
}

async function lookupPlace(query: string): Promise<void> {
  set({ locationStatus: 'searching', error: null, geocodeResults: [] });
  try {
    const results = await geocode(query);
    set({
      locationStatus: 'idle',
      geocodeResults: results,
      error: results.length === 0 ? 'No places matched that.' : null,
    });
  } catch (error) {
    // 401 and an outstanding password change are both answers about the session rather
    // than about the query, and neither is something the user can fix by typing a
    // different place - so they go to the handler that changes screen. Without the
    // second case, a forced change discovered here showed up as a red line above the
    // search box and left the user on a screen that could not do anything.
    if (
      error instanceof ApiError &&
      (error.status === 401 || error.passwordChangeRequired)
    ) {
      handleApiError(error);
      return;
    }
    set({
      locationStatus: 'idle',
      error: error instanceof ApiError ? error.message : 'Location lookup failed.',
    });
  }
}

function pickPlace(result: GeocodeResult): void {
  const label = result.label.split(',')[0] ?? result.label;
  const entry = storeLocation(result.location, label, 'place');
  set({
    location: toAppLocation(entry),
    geocodeResults: [],
    screen: 'question',
    error: null,
  });
}

/**
 * Switch how many questions get asked, mid-run and without losing anything.
 *
 * The simple question set is a strict subset of the advanced one - same ids, same
 * gates - so the answers already given stay valid either way. Going the other way can
 * put a run past the shorter budget, in which case the next render just goes to
 * results, which is what "ask me less" means anyway.
 */
function toggleMode(): void {
  const mode: Mode = state.mode === 'simple' ? 'advanced' : 'simple';
  storeMode(mode);
  set({ mode, questions: buildQuestions(mode, state.units) });
}

/**
 * Miles or kilometres. Purely how things are worded: the question set is rebuilt only
 * because two of its hints quote a radius, and every answer already given survives - the
 * ids and the metres behind them do not depend on the setting.
 */
function setUnits(units: Units): void {
  storeUnits(units);
  set({ units, questions: buildQuestions(state.mode, units) });
}

/** Undo the last answer. Never leaves the question screen: there is always one to show. */
function goBack(): void {
  state.run = back(state.run);
  render();
}

function onAnswer(questionId: string, choice: Answer): void {
  const run = answer(state.questions, state.run, questionId, choice);
  state.run = run;
  if (nextQuestion(state.questions, run) === null) {
    void runSearch();
  } else {
    render();
  }
}

function showResults(places: ScoredPlace[], cached: boolean): void {
  set({
    screen: 'results',
    resultStatus: 'ready',
    places,
    shown: TOP_N,
    cached,
    error: null,
  });
}

/**
 * One search. Client cache first: a repeat of the same answers costs neither a round
 * trip nor a billed call. Null means the call failed and the error is already on screen.
 */
async function searchOnce(
  request: SearchRequest,
): Promise<{ places: ScoredPlace[]; cached: boolean } | null> {
  const hit = readCached(request);
  if (hit) return { places: rank(hit.places, request), cached: true };

  try {
    const response = await search(request);
    writeCached(request, response);
    state.budget = response.budget;
    // Kept in step without a second round trip - the search reply carries the figures
    // it just changed. A cache hit reports them unchanged, which is the honest answer.
    state.usage = response.usage;
    return { places: rank(response.places, request), cached: response.cached };
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

async function runSearch(): Promise<void> {
  if (!state.location) {
    set({ screen: 'location' });
    return;
  }

  // The refresh started at the top of the run may still be in flight, and a search sent
  // on coordinates about to be replaced would be both wrong and billed. Waiting is only
  // visible if the read is slow, hence the spinner ahead of it.
  const located = refreshGps();
  if (gpsRefresh) set({ screen: 'results', resultStatus: 'loading', error: null });
  await located;

  const request = toSearchRequest(state.run.facts, state.location.coords);
  state.lastRequest = request;
  state.widened = null;

  if (!readCached(request)) {
    set({ screen: 'results', resultStatus: 'loading', error: null });
  }

  const first = await searchOnce(request);
  if (!first) return;
  if (first.places.length > 0) {
    showResults(first.places, first.cached);
    return;
  }

  // Nothing at all. An advanced run can land on something rare, and in a thin area that
  // is simply not there - so climb one rung back up the descent and ask again before
  // giving up. Exactly one retry: a second billed call is worth avoiding an empty
  // screen, a third would be spending the budget on guesses.
  const wider = widenFacts(state.run.facts);
  if (!wider) {
    showResults([], first.cached);
    return;
  }

  const widerRequest = toSearchRequest(wider, state.location.coords);
  state.lastRequest = widerRequest;
  // The first pass may have come straight from cache without ever showing a spinner.
  set({ screen: 'results', resultStatus: 'loading', error: null });

  const second = await searchOnce(widerRequest);
  if (!second) return;
  if (second.places.length > 0) {
    state.widened = {
      from: typeLabel(state.run.facts.includedType) ?? 'that',
      to: typeLabel(wider.includedType) ?? 'anywhere nearby',
    };
  }
  showResults(second.places, second.cached);
}

function rank(
  places: Parameters<typeof rankPlaces>[0],
  request: SearchRequest,
): ScoredPlace[] {
  return rankPlaces(places, {
    center: request.center,
    radiusM: request.radiusM,
    priceLevels: request.priceLevels,
    openNowRequested: request.openNow === true,
  });
}

function startOver(): void {
  resetRun();
  // A new run is a new outing: the last one may have ended somewhere else entirely.
  void refreshGps();
  set({ screen: 'question', resultStatus: 'loading' });
}

async function signOut(): Promise<void> {
  await logout();
  resetRun();
  set({
    screen: 'login',
    user: null,
    admin: false,
    mustChangePassword: false,
    budget: null,
    usage: null,
  });
}

// ---------------------------------------------------------------- render

/**
 * Units live up here rather than on the results screen because they are read in two
 * places - the cards and the distance hints - and a setting that only appears next to
 * one of them reads as belonging to it. Signed out there is nothing measured on screen,
 * so it keeps the sign-in page uncluttered.
 */
function masthead(): HTMLElement {
  return el(
    'header',
    { class: 'masthead' },
    el('h1', {}, 'Deci', el('span', {}, 'duous')),
    state.user &&
      el(
        'div',
        { class: 'masthead__tools' },
        unitsToggle(state.units, setUnits),
        // A plain link, not a screen: the dashboard is its own document. Hidden for
        // everyone else as a courtesy - `/api/admin/*` is what refuses them.
        // Hidden mid-change: the dashboard 403s until the password is replaced, so
        // offering the link would only lead somewhere broken.
        state.admin &&
          !state.mustChangePassword &&
          el('a', { class: 'linkish', href: '/admin' }, 'Users'),
        el('button', { class: 'linkish', type: 'button', onclick: () => void signOut() }, 'Sign out'),
      ),
  );
}

/**
 * The login form outlives its renders - see `LoginView`. Kept here rather than rebuilt
 * so that signing in, or failing to, updates the button and the error in place instead
 * of replacing the fields the user (or their password manager) is working in.
 */
let loginForm: LoginView | null = null;

/** Same treatment, and for the same reason: three password fields to preserve. */
let passwordForm: PasswordView | null = null;

function passwordScreen(): HTMLElement {
  if (!passwordForm) {
    passwordForm = passwordView({
      forced: state.mustChangePassword,
      pending: state.pending,
      error: state.error,
      onSubmit: (currentPassword, newPassword) =>
        void doChangePassword(currentPassword, newPassword),
    });
  } else {
    passwordForm.update({ pending: state.pending, error: state.error });
  }
  return passwordForm.node;
}

function loginScreen(): HTMLElement {
  if (!loginForm) {
    loginForm = loginView({
      pending: state.pending,
      error: state.error,
      onSubmit: (username, password) => void doLogin(username, password),
    });
  } else {
    loginForm.update({ pending: state.pending, error: state.error });
  }
  return loginForm.node;
}

function screenView(): HTMLElement {
  switch (state.screen) {
    case 'boot':
      return el('div', { class: 'grow' }, spinner());

    case 'login':
      return loginScreen();

    case 'password':
      return passwordScreen();

    case 'location':
      return locationView({
        status: state.locationStatus,
        error: state.error,
        results: state.geocodeResults,
        onUseGps: () => void useGps(),
        onSearch: (query) => void lookupPlace(query),
        onPick: pickPlace,
      });

    case 'question': {
      const question = nextQuestion(state.questions, state.run);
      if (!question) {
        void runSearch();
        return el('div', { class: 'grow' }, spinner());
      }
      const { asked, estimatedTotal } = progress(state.questions, state.run);
      return questionView({
        question,
        asked,
        estimatedTotal,
        locationLabel: state.location?.label ?? 'you',
        mode: state.mode,
        canGoBack: canGoBack(state.run),
        onAnswer: (choice) => onAnswer(question.id, choice),
        onBack: goBack,
        onSkipToResults: () => void runSearch(),
        onChangeLocation: () => set({ screen: 'location', geocodeResults: [], error: null }),
        onToggleMode: toggleMode,
      });
    }

    case 'results':
      return resultsView({
        status: state.resultStatus,
        places: state.places,
        shown: state.shown,
        error: state.error,
        cached: state.cached,
        units: state.units,
        widened: state.widened,
        maxShown: state.maxResults,
        onShowMore: () => set({ shown: Math.min(state.shown + TOP_N, state.maxResults) }),
        onStartOver: startOver,
        onRetry: () => void runSearch(),
      });
  }
}

function render(): void {
  // Signed in, or gone back to the location screen: the form has served its purpose.
  if (state.screen !== 'login' && loginForm) {
    loginForm.destroy();
    loginForm = null;
  }
  if (state.screen !== 'password' && passwordForm) {
    passwordForm.destroy();
    passwordForm = null;
  }

  // A rebuild detaches every node, and detaching the focused one drops the focus and
  // the caret with it. The login form is the one view that survives its renders, so
  // note where the user was and put them back.
  const focused = document.activeElement;
  const caret =
    focused instanceof HTMLInputElement
      ? { start: focused.selectionStart, end: focused.selectionEnd }
      : null;

  clear(root!);
  root!.append(masthead());

  // The shared-login notice sits above the budget banner: it is the standing state of
  // the account, where the budget one is news about the search that just ran.
  const notice = sharedAccountBanner(state.user, render);
  if (notice) root!.append(notice);

  const banner = budgetBanner(state.budget, render);
  if (banner) root!.append(banner);

  root!.append(screenView());

  // Last, and pinned to the bottom by `margin-top: auto` rather than by the screen
  // above it growing - the results list is a `.stack`, not a `.grow`, so it cannot be
  // relied on to fill the gap. Hidden where there is no account to describe yet.
  if (state.screen !== 'login' && state.screen !== 'password') {
    const usage = usageSummary(state.usage);
    if (usage) root!.append(usage);
  }

  if (focused instanceof HTMLElement && root!.contains(focused)) {
    focused.focus();
    if (caret && focused instanceof HTMLInputElement) {
      focused.setSelectionRange(caret.start, caret.end);
    }
  }
}

/**
 * The meal is baked into the question set at build time, and the set is built once at
 * load. An app opened at noon and picked up again at eight would otherwise still be
 * offering "Just lunch" - and, at the other end of the day, still offering the breakfast
 * short circuit.
 *
 * Rebuilding is safe mid-run for the same reason the mode toggle is: same ids, same
 * gates, so every answer already given still stands. That includes a `meal` already
 * recorded by "Eating right now?", which was true when it was answered and is not
 * retracted by the clock moving on.
 */
function refreshMeal(): void {
  const now = new Date();
  if (mealAt(now) === state.questions.meal) return;
  set({ questions: buildQuestions(state.mode, state.units, now) });
}

// An installed PWA is resumed far more often than it is loaded: coming back to it after
// a walk is exactly the case where the stored fix - and the time of day - is out of date.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  void refreshGps();
  refreshMeal();
});

render();
void boot();
